<template>
  <div class="media-attachment-container">
    <img
      v-if="mode === 'image'"
      :src="attachment.url"
      alt=""
    >
    <bio-digital
      v-else-if="mode === 'biodigital'"
      :attachment="attachment"
    />
    <div
      v-else-if="isAV"
      ref="videoContainer"
      :class="['video-player', 'vjs-custom-skin', mode, ratingClass]"
    >
      <video
        ref="videoElement"
        class="video-js"
      ></video>
    </div>
    <div
      v-if="nextAttachmentQueued"
      class="up-next-overlay"
    >
      Up Next:
      <span class="attachment-title">{{ nextAttachmentTitle }}</span>
      <div class="timer">
        <svg class="timer-bar">
          <circle
            class="timer-outline-backdrop"
            r="45"
            cx="48"
            cy="48"
          />
          <circle
            class="timer-outline"
            r="45"
            cx="48"
            cy="48"
          />
        </svg>
        <button
          class="next-btn"
          @click="$emit('next-clicked')"
        >
          <icon
            class="next-icon"
            name="next"
            variant="white"
          />
        </button>
      </div>
      <button
        class="cancel-btn"
        @click="$emit('cancel-clicked')"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import 'video.js/dist/video-js.css';
import videojs from 'video.js';
import { throttle } from 'lodash';

import BioDigital from '@components/attachments/bio-digital';
import Icon from '@components/singletons/icon';

import RatingMixin from '@/mixins/rating';

const Button = videojs.getComponent('Button');
const BaseComponent = videojs.getComponent('Component');

class SkipBackwardButton extends Button {
  constructor(player, options) {
    super(player, options);
    this.addClass('vjs-skip-button');
    this.addClass('vjs-skip-backward');
    this.controlText('Skip Backward');
  }
  handleClick() {
    this.player().trigger('jumpbackward');
  }
}

class SkipForwardButton extends Button {
  constructor(player, options) {
    super(player, options);
    this.addClass('vjs-skip-button');
    this.addClass('vjs-skip-forward');
    this.controlText('Skip Forward');
  }
  handleClick() {
    this.player().trigger('jumpforward');
  }
}

class ThumbsLabel extends BaseComponent {
  constructor(player, options) {
    super(player, options);
    this.addClass('vjs-thumbs-label');
  }
  createEl() {
    return super.createEl('div', {
      innerHTML: 'Was this helpful?',
    });
  }
}

class ThumbsUpButton extends Button {
  constructor(player, options) {
    super(player, options);
    this.addClass('vjs-thumbs-button');
    this.addClass('vjs-thumbs-up');
    this.controlText('Thumbs Up');
  }
  handleClick() {
    this.player().trigger('thumbsup');
    if (this.hasClass('selected')) {
      this.removeClass('selected');
    } else {
      this.addClass('selected');
      this.player().controlBar.ThumbsDownButton.removeClass('selected');
    }
  }
}

class ThumbsDownButton extends Button {
  constructor(player, options) {
    super(player, options);
    this.addClass('vjs-thumbs-button');
    this.addClass('vjs-thumbs-down');
    this.controlText('Thumbs Down');
  }
  handleClick() {
    this.player().trigger('thumbsdown');
    if (this.hasClass('selected')) {
      this.removeClass('selected');
    } else {
      this.addClass('selected');
      this.player().controlBar.ThumbsUpButton.removeClass('selected');
    }
  }
}

class SaveButton extends Button {
  constructor(player, options) {
    super(player, options);
    this.addClass('vjs-save-button');
    this.controlText('Save');
  }
  handleClick() {
    this.player().trigger('save');
    if (this.hasClass('saved')) {
      this.removeClass('saved');
    } else {
      this.addClass('saved');
    }
  }
}

videojs.registerComponent('SaveButton', SaveButton);
videojs.registerComponent('SkipBackwardButton', SkipBackwardButton);
videojs.registerComponent('SkipForwardButton', SkipForwardButton);
videojs.registerComponent('ThumbsLabel', ThumbsLabel);
videojs.registerComponent('ThumbsUpButton', ThumbsUpButton);
videojs.registerComponent('ThumbsDownButton', ThumbsDownButton);

export default {
  components: { BioDigital, Icon },
  mixins: [RatingMixin],
  props: {
    attachment: {
      required: true,
      type: Object,
    },
    category: {
      type: Object,
    },
    nextAttachmentTitle: {
      type: String,
    },
    nextAttachmentQueued: {
      type: Boolean,
    },
    posterAttachment: {
      type: Object,
    },
    resource: {
      type: Object,
    },
    showBookmark: {
      type: Boolean,
      default: false,
    },
    showRating: {
      type: Boolean,
    },
  },
  emits: ['next-clicked', 'cancel-clicked', 'track-ended'],
  data() {
    return {
      autoplay: false,
      player: null,
      playerInitalized: false,
      preScrubTime: null,
      scrubbed: false,
      totalTime: 0,
      lastTimestamp: 0,
      lastStateSave: 0,
      watchedDuration: 0,
    };
  },
  computed: {
    attachmentState() {
      return this.$store.getters['attachmentStates/get'][this.attachment.id];
    },
    attachmentType() {
      // videojs can't handle audio/mpeg audio type
      // all our audio files should be mp3 anyway
      if (this.attachment.type === 'audio/mpeg') {
        return 'audio/mp3';
      } else if (this.attachment.type === 'video/quicktime') {
        return 'video/mp4';
      } else {
        return this.attachment.type;
      }
    },
    bookmark() {
      return this.$store.getters['bookmarks/get'][this.saveId];
    },
    controlBarChildren() {
      const ratingButtons = this.showRating ? ['ThumbsLabel', 'ThumbsUpButton', 'ThumbsDownButton'] : [];
      const controlBarChildren = [
        'currentTimeDisplay',
        'progressControl',
        'durationDisplay',
        'playToggle',
        ...ratingButtons,
        'SkipBackwardButton',
        'SkipForwardButton',
        'volumePanel',
        'customControlSpacer',
        // 'subsCapsButton',
        'playbackRateMenuButton',
        'fullscreenToggle',
      ];
      if (this.showBookmark && this.mode === 'audio') {
        controlBarChildren.push('SaveButton');
      }
      return controlBarChildren;
    },
    isAV() {
      return this.mode === 'video' || this.mode === 'audio';
    },
    isSaved() {
      return !!this.bookmark && !this.bookmark?.deleted;
    },
    mode() {
      return this.attachment?.mediaType;
    },
    playerChildren() {
      const children = ['MediaLoader', 'PosterImage', 'TextTrackDisplay', 'LoadingSpinner', 'BigPlayButton', 'ErrorDisplay', 'TextTrackSettings', 'ResizeManager'];
      if (this.showBookmark && this.mode === 'video') {
        children.push('SaveButton');
      }
      return children;
    },
    playerOptions() {
      return {
        aspectRatio: '16:9',
        autoplay: this.autoplay,
        controls: true,
        children: this.playerChildren,
        controlBar: {
          children: this.controlBarChildren,
          currentTimeDisplay: true,
          durationDisplay: true,
          timeDivider: true,
        },
        fluid: true,
        language: 'en',
        playbackRates: [0.5, 1.0, 1.5, 2.0],
        poster: this.posterAttachment?.url,
        responsive: true,
        sources: [{
          src: this.attachment.url,
          type: this.attachmentType,
        }],
      };
    },
    ratingClass() {
      if (!this.showRating) {
        return 'hideRating';
      }
    },
    rateableId() {
      return this.attachment.id;
    },
    rateableType() {
      return 'Attachment';
    },
    saveButton() {
      if (this.player) {
        if (this.mode === 'video') {
          return this.player.SaveButton;
        } else if (this.mode === 'audio') {
          return this.player.controlBar?.SaveButton;
        }
      }
    },
    saveId() {
      return `Attachment-${this.attachment?.id}`;
    },
    stateParams() {
      const elapsedSeconds = this.lastStateSave ? this.lastTimestamp - this.lastStateSave : this.watchedDuration;
      return {
        attachmentId: this.attachment?.id,
        currentTimestamp: this.lastTimestamp,
        elapsedSeconds: elapsedSeconds > 0 ? elapsedSeconds : 0,
        player: this.player,
      };
    },
  },
  beforeUnmount() {
    // Dispose of video.js player
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }

    // potentially trigger events on modal close in future
    if (this.watchedDuration > 0) {
      this.sendAttachmentState(this.stateParams, this.$store);
      this.analyticsEvent('endedTrack', { 'AV Timestamp': Math.round(this.lastTimestamp), 'Time Spent Listening': Math.round(this.watchedDuration) });
    }
  },
  mounted() {
    if (this.isAV) {
      // Initialize video.js player
      this.$nextTick(() => {
        if (this.$refs.videoElement) {
          this.player = videojs(this.$refs.videoElement, this.playerOptions, () => {
            // Player is ready - set up all event listeners here
            this.player.on('canplaythrough', this.loadState);
            this.player.on('play', this.play);
            this.player.on('pause', this.pause);
            this.player.on('timeupdate', () => this.timeUpdate(this.player));

            // Initialize custom controls
            this.initPlayer();
          });
        }
      });

      this.analyticsEvent('viewedTrack');
      this.$watch('attachment', (newAtt, oldAtt) => {
        if (oldAtt?.id && oldAtt.id !== newAtt.id) {
          this.autoplay = true;
          this.reset();

          // Recreate player with new source
          if (this.player) {
            this.player.src(this.playerOptions.sources);
            this.player.load();

            // Autoplay the next video
            this.player.ready(() => {
              this.player.play();
            });
          }
        }
      });
    } else {
      // We need to send an attachment state on mount for images and biodigital models as opposed to regular intervals for AV
      this.sendAttachmentState(this.stateParams, this.$store);
      this.$analytics.events.viewedAttachment({ id: this.attachment?.id, name: this.attachment?.assetTitle }, this.resource);
    }

    if (this.showRating) {
      this.$watch('ratingKey', (newVal, oldVal) => {
        if (newVal) {
          this.$store.dispatch('ratings/fetch', { id: this.attachment.id, type: 'Attachment' }).then(() => {
            this.$nextTick(() => {
              if (this.rating?.hasRating() && this.player) {
                const upButton = this.player.controlBar.ThumbsUpButton;
                const downButton = this.player.controlBar.ThumbsDownButton;

                if (this.rating.rating === 1) {
                  upButton.addClass('selected');
                  downButton.removeClass('selected');
                } else if (this.rating.rating === 0) {
                  upButton.removeClass('selected');
                  downButton.addClass('selected');
                }
              }
            });
          });
        }
      }, { immediate: true });
    }

    if (this.showBookmark) {
      this.$watch('saveId', (newVal, oldVal) => {
        if (newVal && newVal !== oldVal) {
          this.checkSaved();
        }
      }, { immediate: true });
    }

    // Watch for posterAttachment changes (e.g., when displayAttachment loads asynchronously)
    // this.$watch('posterAttachment', (newPoster, oldPoster) => {
    //   if (this.player && newPoster?.url !== oldPoster?.url) {
    //     this.player.poster(newPoster?.url || '');
    //   }
    // });
  },
  methods: {
    analyticsEvent(eventName, data = {}) {
      const params = {
        'AV Autoplay': false,
        'AV ID': this.attachment.id,
        'AV Title': this.attachment.assetTitle,
        'AV Resource ID': this.resource?.id,
        'AV Resource Type': this.resource?.type,
        ...data,
      };

      this.$analytics.events[eventName](params);
    },
    checkSaved() {
      this.$nextTick(() => {
        if (this.player && this.isSaved) {
          this.saveButton?.addClass('saved');
        }
      });
    },
    loadState() {
      if (!this.playerInitalized) {
        if (this.attachmentState && !this.attachment.completedState(this.attachmentState)) {
          this.updateLastState(this.attachmentState.currentTimestamp);
          this.player.currentTime(this.attachmentState.currentTimestamp);
        }
        this.playerInitalized = true;
      }
    },
    initPlayer() {
      if (this.player) {
        if (this.showBookmark && this.mode === 'video') {
          this.checkSaved();
        }

        this.player.on('jumpbackward', () => this.jumpButtonClicked(-10));
        this.player.on('jumpforward', () => this.jumpButtonClicked(10));
        this.player.on('thumbsup', () => this.rate('up', this.attachment, this.category));
        this.player.on('thumbsdown', () => this.rate('down', this.attachment, this.category));
        this.player.on('save', this.handleBookmark);
      }
    },
    handleBookmark() {
      if (this.bookmark) {
        const params = {
          id: this.bookmark.id,
          deleted: !this.bookmark.deleted,
        };
        this.$store.dispatch('bookmarks/updateBookmark', params);
      } else {
        const params = {
          bookmarkableType: 'Attachment',
          bookmarkableId: this.attachment.id,
          mobileCreatedAt: Date.now(),
          deleted: false,
        };
        this.$store.dispatch('bookmarks/createBookmark', params);
      }
    },
    jumpButtonClicked(delta) {
      const from = this.player.currentTime();
      this.player.currentTime(from + delta);
      const to = this.player.currentTime();
      this.jump(to, from);
    },
    jump(to, from) {
      const delta = Math.round(to - from);
      const name = delta > 0 ? 'jumpForward' : 'jumpBackward';
      this.analyticsEvent(name, {
        'Total time jumped': Math.abs(delta),
        'Jumped to': Math.round(to),
        'Jumped from': Math.round(from),
      });
    },
    pause() {
      this.analyticsEvent('pauseTrack', { 'AV Timestamp': Math.round(this.lastTimestamp) });
    },
    play() {
      this.analyticsEvent('playTrack', { 'AV Timestamp': Math.round(this.lastTimestamp) });
    },
    // Unconventional syntax but we want to limit the amount of times we are sending
    // states to the api and this syntax allows for wrapping the method with `throttle`
    // to ensure we are throttling all state calls within the component
    sendAttachmentState: throttle((params, store, callback) => {
      const { player, ...apiParams } = params;
      store.dispatch('attachmentStates/send', {attachmentState: apiParams});
      if (apiParams.currentTimestamp && typeof callback === 'function') {
        callback(apiParams.currentTimestamp);
      }
    }, 1000),
    reset() {
      this.playerInitalized = false;
      this.totalTime = 0;
      this.lastTimestamp = 0;
      this.lastStateSave = 0;
      this.watchedDuration = 0;
    },
    timeUpdate(player) {
      if (player.ended()) {
        this.sendAttachmentState(this.stateParams, this.$store, this.updateLastState);
        if (this.player.isFullscreen()) {
          this.player.exitFullscreen();
        }
        this.$emit('track-ended');
      } else {
        const time = player.currentTime();
        // scrubbing event logic
        const scrubbing = player.scrubbing_;

        if (!scrubbing) {
          if (this.scrubbed) {
            const delta = Math.round(time - this.preScrubTime);
            if (delta !== 0) {
              this.jump(time, this.preScrubTime);
            }
          }

          this.preScrubTime = time;
        }

        this.scrubbed = scrubbing;
        // end scrubbing event logic

        const delta = time - this.lastTimestamp;
        // if the difference in timestamp is greater than half a second or negative, this is a scrub
        // timeUpdate event should fire every 250 ms min
        if (delta > 0 && delta < .5) {
          this.watchedDuration = this.watchedDuration + delta;
        }
        this.lastTimestamp = time;

        if (time - this.lastStateSave > 10) {
          this.sendAttachmentState(this.stateParams, this.$store, this.updateLastState);
        }
      }
    },
    updateLastState(time) {
      this.lastStateSave = time;
    },
  },
};
</script>

<style lang="scss" scoped>
.media-attachment-container {
  background: transparent;
  position: relative;
  text-align: center;

  .video-player {
    position: relative;
    width: 100%;
    // Create space below for the control bar to overflow into
    margin-bottom: rem-calc(100);

    :deep(.video-js) {
      width: 100%;
    }
  }

  .video-player :deep(*) {
    video,
    audio,
    .vjs-poster {
      border-radius: rem-calc(4);
    }

    .video-js {
      border-radius: rem-calc(4);
    }

    .vjs-big-play-button {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .vjs-control-bar {
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.85));
      border-radius: 0 0 rem-calc(4) rem-calc(4);
      flex-wrap: wrap;
      height: rem-calc(96);
      line-height: 1em;
      padding: rem-calc(32) rem-calc(40) rem-calc(36) rem-calc(40);
    }

    .vjs-current-time,
    .vjs-progress-control,
    .vjs-duration {
      display: flex !important;
      height: 1em;
      line-height: 1em;
    }

    .vjs-current-time,
    .vjs-duration {
      font-size: rem-calc(12);
      letter-spacing: 1px;
    }

    .vjs-current-time {
      flex-basis: 3em;
      margin: 0;
      order: 1;
    }

    .vjs-progress-control {
      flex-basis: calc(100% - 8em);
      order: 2;
      width: auto;
    }

    .vjs-duration {
      flex-basis: 3em;
      margin: 0;
      order: 3;
    }

    .vjs-play-control {
      margin-left: -0.75em;
      order: 4;
    }

    .vjs-skip-button {
      order: 5,
    }

    .vjs-volume-panel {
      order: 6;

      > button {
        padding-left: 16px;
      }

      .vjs-volume-level {
        background-color: #00d8f8;
        border-radius: 12px;
      }
    }

    .vjs-custom-control-spacer {
      display: block;
      flex: 1;
      order: 7;
    }

    .vjs-thumbs-button, .vjs-thumbs-label {
      order: 8;

      @include breakpoint(small) {
        display: none;
      }
    }

    .vjs-playback-rate {
      order: 9;

      .vjs-icon-placeholder:before {
        content: url('../../assets/icons/speed.svg');
      }

      .vjs-playback-rate-value {
        font-size: 0.8em;
        font-weight: $bold;
        line-height: 2.5;
      }
    }

    .vjs-fullscreen-control {
      margin-right: -0.5em;
      order: 10;

      .vjs-icon-placeholder:before {
        content: url('../../assets/icons/fullscreen.svg');
        line-height: 1.2;
      }
    }

    .vjs-play-progress {
      background-color: #00d8f8;
    }

    .vjs-slider {
      background-color: #b3b3b3;
    }

    .vjs-progress-holder, .vjs-slider-bar {
      border-radius: 12px;
    }

    .vjs-skip-backward .vjs-icon-placeholder:before {
      content: url('../../assets/icons/skip-backward.svg');
    }

    .vjs-skip-forward .vjs-icon-placeholder:before {
      content: url('../../assets/icons/skip-forward.svg');
    }

    .vjs-thumbs-up, .vjs-thumbs-down {
      .vjs-icon-placeholder:before {
        background-image: url('../../assets/icons/thumb-outline-ffffff.svg');
        background-position: center 36%;
        background-repeat: no-repeat;
        background-size: 20px;
        content: '';
        transform: none !important;
      }

      &.selected {
        .vjs-icon-placeholder:before {
          background-image: url('../../assets/icons/thumb-filled-ffffff.svg');
        }
      }
    }

    .vjs-thumbs-down {
      margin-right: rem-calc(32);

      .vjs-icon-placeholder:before {
        background-position: center 64%;
        transform: rotate(180deg) !important;
      }
    }

    .vjs-thumbs-label {
      font-size: rem-calc(13);
      font-weight: $semi-bold;
      line-height: 41px;
      margin-right: rem-calc(12);
    }

    // for customizing the playback speed menu, disabling the menu for now
    // because you can just click through the speeds

    .vjs-menu {
      display: none !important;
    }

    .vjs-button:hover {
      .vjs-icon-placeholder:before, .vjs-playback-rate-value {
        transform: scale(1.15);
      }
    }

    // .vjs-menu {
    //   left: 0.3em;
    //   width: 2.5em;

    //   .vjs-menu-content {
    //     background-color: #000000;
    //     bottom: 2em;

    //     li {
    //       font-size: 0.8em;
    //       font-weight: $semi-bold;

    //       &.vjs-selected {
    //         background-color: #000000;
    //         color: #00deff;
    //       }

    //       &:hover {
    //         opacity: 0.9;
    //       }
    //     }
    //   }
    // }

    @include breakpoint(small) {
      .vjs-control-bar {
        background: black;
      }
    }

    .vjs-save-button {
      opacity: 0;
      visibility: hidden;

      .vjs-icon-placeholder:before {
        background-image: url('../../assets/icons/bookmark-ffffff.svg');
        content: '';
        transform: none !important;
      }

      &.saved {
        .vjs-icon-placeholder:before {
          background-image: url('../../assets/icons/bookmark-active-ffffff.svg');
        }
      }
    }


    .video-js {
      &.vjs-user-active,
      &.vjs-paused {
        .vjs-save-button {
          visibility: visible;
          opacity: 1;
        }
      }
    }
  }


  .audio :deep(*) {
    .vjs-poster {
      display: block !important; // Overrides the default behavior to hide the poster once audio has started allowing the poster to display during playback
    }

    .vjs-control-bar {
      @include breakpoint(small) {
        bottom: 0;
      }

      .vjs-save-button {
        order: 10;

        .vjs-icon-placeholder:before {
          background-position: 75% 33%;
          background-repeat: no-repeat;
          background-size: 18px;
        }
      }

      .vjs-playback-rate {
        margin-right: -0.4em;
      }
    }
  }

  .video {
    :deep(.vjs-save-button) {
      background-color: rgba(0, 0, 0, 0.65);
      border-radius: rem-calc(4);
      height: rem-calc(48);
      position: absolute;
      right: 16px;
      top: 16px;
      transition: 0.5s ease-in-out all;
      width: 48px;

      .vjs-icon-placeholder:before {
        background-position: 50% 45%;
        background-repeat: no-repeat;
        background-size: 24px;
      }

      @include breakpoint(small) {
        height: rem-calc(32);
        right: 8px;
        top: 24px;
        width: rem-calc(32);

        .vjs-icon-placeholder:before {
          background-size: 20px;
        }
      }
    }

    @include breakpoint(small) {
      // Gives space for the controls to appear on smaller devices
      padding-bottom: 96px;
    }
  }

  .hideRating :deep(*) {
    .vjs-thumbs-button, .vjs-thumbs-label {
      display: none !important;
    }
  }

  .up-next-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    text-align: center;
    color: #ffffff;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .attachment-title {
      font-weight: $bold;
      font-size: rem-calc(24);
      display: block;
      margin-top: rem-calc(16);
    }

    .timer {
      margin-top: rem-calc(36);
      width: rem-calc(96);
      height: rem-calc(96);
      background-color: rgba(216, 216, 216, 0.3);
      border-radius: 100%;
      position: relative;

      .next-btn {
        width: 100%;
        height: 100%;
        background: transparent;
        pointer-events: auto;

        .next-icon {
          width: rem-calc(30);
          height: rem-calc(30);
          margin: 0 auto;
        }
      }

      .timer-bar {
        position: absolute;
        top: 0;
        left: 0;
        fill: transparent;
        height: 100%;
        width: 100%;
        transform-origin: 48px 48px;
        transform: rotate(-90deg);
        pointer-events: none;

        .timer-outline-backdrop {
          stroke: rgba(216, 216, 216, 0.3);
          stroke-width: rem-calc(6);
        }

        .timer-outline {
          $circumference: 282px; // Circumference of the circle element (2 * 3.14 * 45 [radius]) (can't be rem)
          stroke: #ffffff;
          stroke-width: rem-calc(6);
          stroke-linecap: round;
          stroke-dasharray: $circumference;
          animation: timer 5s linear;

          @keyframes timer {
            from {
              stroke-dashoffset: $circumference;
            }
            to {
              stroke-dashoffset: 0;
            }
          }
        }

      }
    }

    .cancel-btn {
      background: transparent;
      color: inherit;
      margin-top: rem-calc(36);
    }
  }
}
</style>
